import React, { useState, useContext } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import { generateDate, months, slots } from "./util/calendar";
import { cn } from "./util/cn";
import { FurnitureSearch } from "../../App";
import { postReq } from "../../configs/ApiUrl";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import Button from "../Button";
import { Typography, Paper, Box, Stack, IconButton } from "@mui/material";
import Title from "../Title";

dayjs.extend(isSameOrBefore);

function Calendar() {
  const { t } = useTranslation(["calendarPage"]);
  const [searchTerm, setSearchTerm] = useContext(FurnitureSearch);
  const [today, setToday] = useState(dayjs());
  const [selectDate, setSelectDate] = useState(dayjs());
  const [chosenDate, setChosenDate] = useState(null);
  const [time, setTime] = useState({
    slots: slots,
    buttonBackground: Array(slots.length).fill("bg-stone-300 text-gray-400"),
    selected: Array(slots.length).fill(false),
  });
  const [selectedTimes, setSelectedTimes] = useState({
    selectedDates: [],
    selectedSlots: [],
  });
  const [errorWarn, setErrorWarn] = useState("");
  const [next, setNext] = useState({
    button: "bg-stone-300 text-gray-400",
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isSeller, grandTotal, shoppingCart } = location.state || {};

  function handleDateClick(date) {
    if (date.isSameOrBefore(dayjs(), "day")) {
      alert(t("pastDateAlert"));
      return;
    }
    setSelectDate(date);
    setChosenDate({
      day: date.date(),
      month: date.month() + 1,
      year: date.year(),
    });
    setTime({
      ...time,
      buttonBackground: Array(slots.length).fill("bg-stone-400 text-black"),
      selected: Array(slots.length).fill(false),
    });
  }

  const formatDateAndTime = (dateString, timeString) => {
    const [day, month, year] = dateString.split(".");
    const date = new Date(year, month - 1, day);
    return {
      date: date,
      time: timeString.trim(),
    };
  };

  const removeTime = (indexToRemove) => {
    setSelectedTimes((prevSelected) => {
      const updatedDates = prevSelected.selectedDates.filter(
        (_, index) => index !== indexToRemove
      );
      const updatedSlots = prevSelected.selectedSlots.filter(
        (_, index) => index !== indexToRemove
      );

      const removedDate = prevSelected.selectedDates[indexToRemove];
      const selectedDateStr = `${chosenDate?.day}.${chosenDate?.month}.${chosenDate?.year}`;

      if (removedDate === selectedDateStr) {
        setTime({
          ...time,
          buttonBackground: Array(slots.length).fill("bg-stone-400 text-black"),
          selected: Array(slots.length).fill(false),
        });
      }

      setNext({
        button:
          updatedDates.length >= 3
            ? "bg-cromboOrange text-white"
            : "bg-stone-300 text-gray-400",
      });

      return {
        selectedDates: updatedDates,
        selectedSlots: updatedSlots,
      };
    });
  };

  function timeSlotBoolean(index) {
    const newSelected = [...time.selected];
    newSelected[index] = !newSelected[index];
    setTime({
      ...time,
      selected: newSelected,
      buttonBackground: newSelected.map((selected) =>
        selected ? "bg-cromboOrange text-white" : "bg-stone-400 text-black"
      ),
    });

    const selectedDate = `${chosenDate.day}.${chosenDate.month}.${chosenDate.year}`;
    const currentSlots = time.slots.filter((_, i) => newSelected[i]).join(", ");

    const datesAdded = [...selectedTimes.selectedDates];
    const slotsAdded = [...selectedTimes.selectedSlots];
    const dateIndex = datesAdded.indexOf(selectedDate);
    if (currentSlots) {
      if (dateIndex !== -1) {
        slotsAdded[dateIndex] = currentSlots;
      } else {
        datesAdded.push(selectedDate);
        slotsAdded.push(currentSlots);
      }
    } else if (dateIndex !== -1) {
      datesAdded.splice(dateIndex, 1);
      slotsAdded.splice(dateIndex, 1);
    }

    setSelectedTimes({ selectedDates: datesAdded, selectedSlots: slotsAdded });
    setNext({
      button:
        datesAdded.length >= 3
          ? "bg-cromboOrange text-white"
          : "bg-stone-300 text-gray-400",
    });
  }

  const handleButtonClick = async () => {
    if (selectedTimes.selectedDates.length < 3) {
      setErrorWarn(t("minimumThree"));
      return;
    }

    const selectedTimeDateSlots = selectedTimes.selectedDates.map(
      (date, index) => {
        return formatDateAndTime(date, selectedTimes.selectedSlots[index]);
      }
    );

    if (isSeller) {
      try {
        setIsLoading(true);
        if (selectedTimeDateSlots?.length) {
          selectedTimeDateSlots.forEach((slot, index) => {
            searchTerm?.productFormData.append(
              `pickUpSlots[${index}][date]`,
              slot.date
            );
            searchTerm?.productFormData.append(
              `pickUpSlots[${index}][time]`,
              slot.time
            );
          });
        }

        const productResp = await postReq(
          "/product/add",
          searchTerm?.productFormData
        );
        if (productResp?.success) {
          message.success(t("uploadSuccess"), 2);
          setSearchTerm({
            ...searchTerm,
            productFormData: {},
          });
          navigate("/selling-add-success");
        } else {
          message.error(t("uploadFailed"), 1);
        }
      } catch (error) {
        message.error(t("uploadFailed"), 1);
      } finally {
        setIsLoading(false);
      }
    } else {
      setSearchTerm({
        ...searchTerm,
        deliverySlots: selectedTimeDateSlots,
      });
      navigate("/checkout", {
        state: {
          grandTotal,
          shoppingCart,
          deliverySlots: selectedTimeDateSlots,
        },
      });
    }
  };

  return (
    <div className='flex flex-col items-center pb-6 h-full'>
      <Title text={t("title")} />

      {/* Calendar */}
      <div className='flex w-10/12 mx-auto items-center justify-center max-w-md bg-white rounded-xl pt-3 pb-10 px-3'>
        <div className='w-96 h-96'>
          <div className='flex justify-between'>
            <h1 className='font-semibold'>
              {months[today.month()]}, {today.year()}
            </h1>
            <div className='flex items-center gap-5'>
              <GrFormPrevious
                className='w-5 h-5 cursor-pointer'
                onClick={() => {
                  if (
                    today.month(today.month() - 1).$M < dayjs().$M &&
                    today.month(today.month() - 1).$y <= dayjs().$y
                  ) {
                    return;
                  }
                  setToday(today.month(today.month() - 1));
                }}
              />
              <h1
                className='cursor-pointer'
                onClick={() => setToday(dayjs())}>
                Today
              </h1>
              <GrFormNext
                className='w-5 h-5 cursor-pointer'
                onClick={() => setToday(today.month(today.month() + 1))}
              />
            </div>
          </div>

          <div className='w-full grid grid-cols-7 text-gray-500'>
            {["S", "M", "T", t("Wednesday"), "T", "F", t("Saturday")].map(
              (day, index) => (
                <h1
                  key={index}
                  className='h-14 grid place-content-center text-sm'>
                  {day}
                </h1>
              )
            )}
          </div>

          <div className='w-full grid grid-cols-7'>
            {generateDate(today.month(), today.year()).map(
              ({ date, currentMonth, today }, index) => (
                <div
                  key={index}
                  className='h-14 border-t grid place-content-center text-sm'>
                  <h1
                    className={cn(
                      currentMonth ? "" : "text-gray-400",
                      today ? "bg-gray-500 text-white" : "",
                      selectDate.toDate().toDateString() ===
                        date.toDate().toDateString()
                        ? "bg-cromboOrange text-white"
                        : "",
                      "h-10 w-10 grid place-content-center rounded-full transition-all cursor-pointer"
                    )}
                    onClick={() => handleDateClick(date)}>
                    {date.date()}
                  </h1>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <Box
        width='100%'
        maxWidth={420}
        mt={6}
        px={3}>
        {/* Time Slots Section */}
        <Typography
          variant='h6'
          fontWeight='bold'
          gutterBottom>
          {t("slots")}
        </Typography>
        <Box
          display='grid'
          gridTemplateColumns='repeat(3, minmax(0, 1fr))'
          gap={1.5}
          mb={4}>
          {time.slots.map((slot, index) => (
            <Box
              key={index}
              onClick={() => {
                if (!chosenDate) {
                  alert(t("noDateAlert"));
                  return;
                }
                timeSlotBoolean(index);
              }}
              className={`${time.buttonBackground[index]} text-sm text-center p-2 rounded-xl font-semibold cursor-pointer`}>
              {slot}
            </Box>
          ))}
        </Box>

        {/* Chosen Slots Section */}
        <Typography
          variant='h6'
          fontWeight='bold'
          gutterBottom>
          {t("chosenSlots")}
        </Typography>
        {selectedTimes.selectedDates.length > 0 ? (
          <Stack
            spacing={2}
            mb={4}>
            {selectedTimes.selectedDates.map((item, i) => (
              <Paper
                key={i}
                elevation={1}
                sx={{
                  backgroundColor: "gray",
                  color: "white",
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: 2,
                  cursor: "pointer",
                }}>
                <Box>
                  <Typography
                    fontWeight='bold'
                    fontSize={14}
                    color='#f5f5f4'>
                    {item}
                  </Typography>
                  <Typography
                    fontSize={13}
                    color='#ffffff'
                    fontWeight={500}>
                    {selectedTimes.selectedSlots[i]}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => removeTime(i)}
                  sx={{ color: "#ffffff" }}
                  size='small'>
                  <IoMdClose size={20} />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            mb={4}>
            {t("minimumThree")}
          </Typography>
        )}

        {/* Action Button */}
        <Box
          textAlign='center'
          mt={2}>
          <Button
            variant='main'
            onClick={handleButtonClick}>
            <Typography
              variant='body1'
              color='white'
              fontWeight='bold'
              textAlign='center'>
              {isSeller ? t("uploadButton") : t("proceedButton")}
            </Typography>
          </Button>
          {errorWarn && (
            <Typography
              color='error'
              mt={2}>
              {errorWarn}
            </Typography>
          )}
        </Box>
      </Box>
    </div>
  );
}

export default Calendar;
